// @ts-ignore
import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";

import {
    AnalyticsEventPayload,
    AnalyticsListener,
    UIAnalyticsEvent
} from "@atlaskit/analytics-next";
// TODO: importing this module seems to cause some "Cannot find module tslib" errors
import { Environment, XID } from "@atlassian/atl-cross-domain-tracking";
import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";
import type { envType, userType } from "@atlassiansox/analytics-web-client";

import { Cookie } from "../../utils/cookie";
import { Tracking } from "../atl-analytics/tracking";

const getGASv3Environment = (envTypes: typeof envType): string => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
            return envTypes.PROD;
        case "staging":
            return envTypes.STAGING;
        case "development":
            return envTypes.DEV;
        default:
            return envTypes.LOCAL;
    }
};

const getXidEnvironment = (): Environment => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
            return Environment.PROD;
        case "staging":
            return Environment.STAGE;
        case "development":
            return Environment.DEV;
        default:
            return Environment.LOCAL;
    }
};

const shouldUseStargate = (): boolean => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
        case "staging":
            return true;
        case "development":
        default:
            return false;
    }
};

const getAtlassianUserId = () => {
    const userId = Cookie.readCookie("__aid_user_id");
    if (userId) {
        return decodeURIComponent(userId);
    }
    return null;
};

const useAnalyticsOptOut = () => {
    const [analyticsOptOut, setAnalyticsOptOut] = useState(true);

    useEffect(() => {
        const OptanonConsentValue = new RegExp("OptanonConsent=([^;]+)").exec(
            document.cookie
        )?.[1];
        const currentAnalyticsOptOut = OptanonConsentValue
            ? decodeURIComponent(OptanonConsentValue).includes(",2:0")
            : false;

        if (analyticsOptOut !== currentAnalyticsOptOut) {
            setAnalyticsOptOut(currentAnalyticsOptOut);
        }
    }, [analyticsOptOut]);

    return [analyticsOptOut];
};

const getAnonIdUsedByFeatureGatesClientInSSR = () =>
    window.featureGateBootstrapValues.identifiers.analyticsAnonymousId;

export interface AnalyticsWebClientContextType {
    analyticsWebClientInstance: AnalyticsWebClient | null;
}

const AnalyticsWebClientContext = createContext<AnalyticsWebClientContextType>({
    analyticsWebClientInstance: null
});

export const WacAnalyticsListener = ({ children }: PropsWithChildren) => {
    const analyticsClient = useAnalyticsClient();
    const [analyticsOptOut] = useAnalyticsOptOut();
    const loginUserDetectedCookie = Cookie.readCookie("login_user_detected");

    const onEvent = useCallback(
        (event: UIAnalyticsEvent, channel?: string | undefined) => {
            if (!analyticsClient || analyticsOptOut) {
                return;
            }

            if (channel === "signup") {
                const analyticsDataList = [...event.context, event.payload];
                const analyticsEventMagnolia = analyticsDataList.reduce(
                    (mergedAnalyticsEvent, currentAnalyicsEvent) => {
                        return {
                            ...mergedAnalyticsEvent,
                            ...currentAnalyicsEvent
                        };
                    },
                    {}
                );
                Tracking.trackEvent(
                    analyticsEventMagnolia["eventName"],
                    analyticsEventMagnolia
                );
                return;
            }

            // TODO - in the future add any default context
            const processedEventPayload: AnalyticsEventPayload = {
                actionSubject: "component",
                eventType: "ui",
                source: "default",
                ...event.payload,
                attributes: {
                    ...(event.payload["attributes"] || {}),
                    loginUserDetected: loginUserDetectedCookie || "false"
                }
            };
            if (!!window?.cmsPageVariant) {
                processedEventPayload["attributes"].cmsPageVariant =
                    window.cmsPageVariant;
            }

            const ANALYTICS_TYPE = process.env["ANALYTICS_TYPE"] || "console";
            if (ANALYTICS_TYPE === "console") {
                console.log({ event: processedEventPayload });
                void analyticsClient.onEvent(null, processedEventPayload);
            } else {
                void analyticsClient.onEvent(null, processedEventPayload);
            }
        },
        [analyticsClient, analyticsOptOut, loginUserDetectedCookie]
    );

    return (
        /**
         * Current channels are:
         * - atl-global: For all auto-generated events from InitializeAutoAtlAnalytics
         * - marketing: For events generated via the React components themselves
         *
         * TODO - can add in the FabricEventsListeners to catch atlasKit and fabric components etc.
         */
        <AnalyticsListener channel={"*"} onEvent={onEvent}>
            {children}
        </AnalyticsListener>
    );
};
type WacAnalyticsClientProviderProps = PropsWithChildren;
export const WacAnalyticsClientProvider = ({
    children
}: WacAnalyticsClientProviderProps) => {
    const [analyticsWebClientInstance, setAnalyticsWebClientInstance] =
        useState<AnalyticsWebClient | null>(null);
    useEffect(() => {
        if (typeof window !== "undefined") {
            void import("@atlassiansox/analytics-web-client").then((Module) => {
                const AnalyticsWebClientLib =
                    Module.default as unknown as typeof AnalyticsWebClient;
                const envTypes = Module.envType as unknown as typeof envType;
                const userTypes = Module.userType as unknown as typeof userType;
                const client = new AnalyticsWebClientLib(
                    {
                        env: getGASv3Environment(envTypes),
                        product: "marketing",
                        version: "1.0.0",
                        locale: "en-us"
                    },
                    {
                        useStargate: shouldUseStargate(),
                        xidConsent: true,
                        xidPromiseFn: () =>
                            new XID(
                                getXidEnvironment()
                            ).getXidCallbackForPromise(),
                        // If SSR wasn't given an anonymous ID cookie, then it will generate one and pass it down.
                        // This function will ensure the analytics client uses the same one instead of generating another.
                        customAnonymousIdGenerator:
                            getAnonIdUsedByFeatureGatesClientInSSR
                    }
                );

                const aaid = getAtlassianUserId();
                if (aaid) {
                    client.setUserInfo(userTypes.ATLASSIAN_ACCOUNT, aaid);
                }

                // If the FeatureGates and analytics clients are using different identifiers,
                // it means that the client already had an anonymousId in localStorage and/or cookies,
                // but SSR was unable to use it, either because the client had blocked sending cookies,
                // or because there was some issue extracting it.
                // An identifier mismatch means we won't be able to include this session in the experiment metrics.
                // We capture an operational event here as a way to tell how much data we've lost in our experiment results.
                const featureGatesAnonId =
                    getAnonIdUsedByFeatureGatesClientInSSR();
                if (
                    getAnonIdUsedByFeatureGatesClientInSSR() !==
                    client.getAnonymousId()
                ) {
                    client.sendOperationalEvent({
                        action: "mismatched",
                        actionSubject: "anonymousId",
                        source: "awcInitialization",
                        attributes: {
                            featureGatesAnonId
                        }
                    });
                }

                setAnalyticsWebClientInstance(client);
            });
        }
    }, []);
    return (
        <AnalyticsWebClientContext.Provider
            value={{ analyticsWebClientInstance }}
        >
            {children}
        </AnalyticsWebClientContext.Provider>
    );
};

export const useAnalyticsClient = () => {
    const context = useContext(AnalyticsWebClientContext);
    if (!context) {
        throw new Error(
            "calls to useAnalyticsClient must be wrapped in <WacAnalyticsClientProvider>!"
        );
    }

    return context.analyticsWebClientInstance;
};
